import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { useRefetch } from '../lib/connection'
import { plural, rangeLabel } from '../pages/wages/format'
import styles from './ExportReviewNotices.module.css'

// D6.5 (approved by Kerrin, 2026-10-02). When a recalculation is queued for
// worker/days a produced export contains, the person who produced it is told
// here, and the Pay period shows a banner to people who may review pay. Both
// come from GET /api/wages/review-notices, which checks membership and the pay
// power each time, so a person who has lost either sees nothing. The text says
// the period is being recalculated and the earlier file may need review. It
// never says an amount changed, and acknowledging it does not mean a provider
// received a replacement: nothing is resent, rewritten or blocked.

export interface ReviewNotice {
  id: string
  export_id: string
  period_from: string
  period_to: string
  last_queued_at: string
  queue_events: number
  affected_days: number
  filename: string
  exported_at: string
}
interface Notices {
  mine: ReviewNotice[]
  period: ReviewNotice[]
}

const at = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' })}`
}
export const reviewLink = (n: ReviewNotice) => `/wages?from=${n.period_from}&to=${n.period_to}&export=${n.export_id}`

// Fetches quietly: a failure shows nothing rather than an error on every
// screen. Reloading the page, or coming back a week later, fetches it again.
function useNotices(period?: { from: string; to: string } | null) {
  const { user } = useAuth()
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const [data, setData] = useState<Notices>({ mine: [], period: [] })
  const from = period?.from
  const to = period?.to
  const load = useCallback(async () => {
    if (!userRef.current) return
    try {
      const qs = from && to ? `?from=${from}&to=${to}` : ''
      const r = await apiJson<Notices>(userRef.current, `/api/wages/review-notices${qs}`)
      setData({ mine: r?.mine || [], period: r?.period || [] })
    } catch {
      setData({ mine: [], period: [] })
    }
  }, [uid, from, to])
  useEffect(() => {
    load()
  }, [load])
  useRefetch(load)
  const acknowledge = async (id: string) => {
    try {
      await apiJson(userRef.current, `/api/wages/review-notices/${id}/acknowledge`, { method: 'POST', body: {} })
    } catch {
      /* it stays; the next fetch says so */
    }
    await load()
  }
  return { data, acknowledge }
}

const body = (n: ReviewNotice) => (
  <>
    <strong>{rangeLabel(n.period_from, n.period_to)} is being recalculated.</strong> The payroll file {n.filename} you exported on {at(n.exported_at)} may need review:{' '}
    {plural(n.affected_days, 'worker/day', 'worker/days')} in it {n.affected_days === 1 ? 'was' : 'were'} queued to recalculate. This does not mean any amount has changed, and nothing has been resent or replaced.
  </>
)

// Across the top of the app, for the person who produced the export.
export function ExporterNotices() {
  const { data, acknowledge } = useNotices(null)
  if (!data.mine.length) return null
  return (
    <div className={styles.stack} data-testid="export-review-notices" role="region" aria-label="Exports that may need review" tabIndex={0}>
      {data.mine.map((n) => (
        <div key={n.id} className={styles.notice} role="status" data-testid="export-review-notice" data-export-id={n.export_id}>
          <span className={styles.text}>{body(n)}</span>
          <span className={styles.actions}>
            <Link className={styles.link} to={reviewLink(n)}>
              Open the export and period
            </Link>
            <button type="button" className={styles.ack} onClick={() => acknowledge(n.id)}>
              I have seen this
            </button>
          </span>
        </div>
      ))}
    </div>
  )
}

// On the Pay period, for people who may review pay.
export function PeriodReviewBanner({ from, to }: { from: string; to: string }) {
  const { data, acknowledge } = useNotices(from && to ? { from, to } : null)
  if (!data.period.length) return null
  return (
    <div data-testid="period-review-banner">
      {data.period.map((n) => (
        <div key={n.id} className={styles.banner} role="status" data-testid="period-review-notice" data-export-id={n.export_id}>
          <span className={styles.text}>
            <strong>An export covering {rangeLabel(n.period_from, n.period_to)} may need review.</strong> The file {n.filename} (exported {at(n.exported_at)}) includes{' '}
            {plural(n.affected_days, 'worker/day', 'worker/days')} now queued to recalculate. This does not mean any amount has changed. The earlier file is kept as it was, and nothing is resent.
          </span>
          <button type="button" className={styles.ack} onClick={() => acknowledge(n.id)}>
            Dismiss for me
          </button>
        </div>
      ))}
    </div>
  )
}
